import { createSlice } from '@reduxjs/toolkit';
import { createSliceHook, makeActionCreator } from '../helpers';
import { type QueryTicketMessageRequest } from '@restore/queries/queries.types';
import {
  QCQueriesFetchAction,
  QCQueriesFetchSuccessAction,
  QCQueriesState,
  QCQueryFetchSuccessAction,
  QCQueryRecipientsFetchAction,
  QCQueryRecipientsFetchSuccessAction, QCQueryFetchAction,
  UpdateCurrentQueryAction,
  UpdateCurrentQuerySuccessAction,
} from './qcQueries.types';

const initialState: QCQueriesState = {
  loading: false,
  error: undefined,
  data: null,
  currentQuery: null,
  currentQueryLoading: false,
  currentQueryError: undefined,
  currentQueryUpdating: false,
  currentQueryUpdateError: undefined,
  recipients: null,
  recipientsLoading: false,
  recipientsError: undefined,
  filters: {},
};

const { actReq, actReject, actResolve } = makeActionCreator<QCQueriesState>();

export const qcQueriesSlice = createSlice({
  name: 'qcQueries',
  initialState,
  reducers: {
    fetchQCQueries: actReq<QCQueriesFetchAction['payload']>(),
    fetchQCQueriesSuccess:
      actResolve<QCQueriesFetchSuccessAction['payload']>('data'),
    fetchQCQueriesFailure: actReject<Error>(),
    fetchCurrentQuery: actReq<QCQueryFetchAction['payload']>('currentQueryLoading'),
    fetchCurrentQuerySuccess: actResolve<QCQueryFetchSuccessAction['payload']>(
      'currentQuery',
      'currentQueryLoading',
      'currentQueryError'
    ),
    fetchCurrentQueryFailure: actReject(
      'currentQueryError',
      'currentQueryLoading'
    ),
    clearCurrentQuery: state => {
      state.currentQuery = null;
      state.currentQueryLoading = false;
      state.currentQueryError = undefined;
    },
    updateCurrentQuery: actReq<UpdateCurrentQueryAction['payload']>('currentQueryUpdating'),
    updateCurrentQuerySuccess: actResolve<UpdateCurrentQuerySuccessAction['payload']>(
      'currentQuery',
      'currentQueryUpdating',
      'currentQueryUpdateError'
    ),
    updateCurrentQueryFailure: actReject(
      'currentQueryUpdateError',
      'currentQueryUpdating'
    ),
    // Create Query
    createQCQuery: actReq<QueryTicketMessageRequest>(),
    createQCQuerySuccess: actResolve<null>(null, 'loading'),
    createQCQueryFailure: actReject<Error>(),
    // Fetch Recipients
    fetchQCQueryRecipients:
      actReq<QCQueryRecipientsFetchAction['payload']>('recipientsLoading'),
    fetchQCQueryRecipientsSuccess: actResolve<
      QCQueryRecipientsFetchSuccessAction['payload']
    >('recipients', 'recipientsLoading', 'recipientsError'),
    fetchQCQueryRecipientsFailure: actReject(
      'recipientsError',
      'recipientsLoading'
    ),
    setFilters: (state, action) => {
      const { filterQuery, filterValue } = (
        action as unknown as {
          payload: {
            filterQuery?: keyof QCQueriesState['filters'];
            filterValue: string;
          };
        }
      ).payload;
      if (!filterQuery) return;
      state.filters = {
        ...state.filters,
        [filterQuery]: filterValue,
      } as QCQueriesState['filters'];
    },
  },
});

export const {
  fetchQCQueries,
  fetchQCQueriesSuccess,
  fetchQCQueriesFailure,
  fetchCurrentQuery,
  fetchCurrentQuerySuccess,
  fetchCurrentQueryFailure,
  clearCurrentQuery,
  createQCQuery,
  createQCQuerySuccess,
  createQCQueryFailure,
  fetchQCQueryRecipients,
  fetchQCQueryRecipientsSuccess,
  fetchQCQueryRecipientsFailure,
  updateCurrentQuery,
  updateCurrentQuerySuccess,
  updateCurrentQueryFailure,
} = qcQueriesSlice.actions;

export const actions = {
  ...qcQueriesSlice.actions,
};

export const useQCQueriesSlice = createSliceHook(qcQueriesSlice.name, actions);
